import { memo, useCallback, useMemo } from "react";
import { Spinner, Table, Row, Col, Form, Card, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  FaBuilding,
  FaGlobe,
  FaBullseye,
  FaDollarSign,
  FaInfoCircle,
  FaFilePdf,
  FaUsers,
  FaChartLine,
  FaPercentage,
  FaIndustry,
} from "react-icons/fa";
import { BsFileArrowDown } from "react-icons/bs";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { useGetPitchTeamMembers } from "../../api/queries/Pitch/useGetPitchTeamMembers";
import { useGetPitchFiles } from "../../api/queries/Pitch/useGetPitchFiles";
import { useGetPitch } from "../../api/queries/Pitch/useGetPitch";

const LOADING_MIN_HEIGHT_STYLE: React.CSSProperties = { minHeight: "3rem" };
const ICON_BUTTON_STYLE_30: React.CSSProperties = {
  width: "30px",
  height: "30px",
};

function ViewPitch() {
  const { id } = useParams<{ id: string }>();
  const pitchId = id || "";

  const { data: pitchData, isPending } = useGetPitch({
    id: Number(pitchId) || 0,
    enabled: !!pitchId,
  });

  const { data: teamMembersData, isLoading: isTeamLoading } =
    useGetPitchTeamMembers({ id: pitchId, enabled: !!pitchId });

  const { data: pitchFilesData, isLoading: isFilesLoading } = useGetPitchFiles({
    id: pitchId,
    enabled: !!pitchId,
  });

  const filesUploaded = useMemo(
    () => pitchFilesData ?? [],
    [pitchFilesData]
  );

  const downloadFile = useCallback(async (fileId: number) => {
    try {
      const response = await axiosInstance.get(
        getAPIURL(`/Pitch/pitch-file/${fileId}/download`),
        { responseType: "blob" }
      );
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/['"]/g, "")
        : `downloaded-file-${fileId}`;
      const fileURL = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  }, []);

  if (isPending) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!pitchData) {
    return <div className="text-center py-5 text-muted">Pitch not found</div>;
  }

  const p = pitchData.pitchViewModel.pitch;

  return (
    <div className="container my-4">
      {/* Pitch Status */}
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">Status</h5>
        <Form.Select value={p.status} disabled>
          <option value="Submitted">Submitted</option>
          <option value="Accepted">Accepted</option>
          <option value="Rejected">Rejected</option>
          <option value="Draft">Draft</option>
        </Form.Select>
      </div>

      {/* Pitch Details */}
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">Pitch Details</h5>
        <Row>
          {/* Sector */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBuilding size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Sector</strong>
                <div className="text-muted small">{p.sector || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Region */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Region</strong>
                <div className="text-muted small">{p.region || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Investment Type */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Investment Type</strong>
                <div className="text-muted small">
                  {p.investmentType || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Investment Ticket Size */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Investment Ticket Size</strong>
                <div className="text-muted small">
                  {p.investmentTicketSize || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Amount Sought */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Amount Sought</strong>
                <div className="text-muted small">${p.amountSought || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Description */}
          <Col md={12} className="mb-3">
            <div className="d-flex align-items-start">
              <FaInfoCircle size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Description</strong>
                <div className="text-muted small">
                  {p.companyDescription || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Mission */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIndustry size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Mission</strong>
                <div className="text-muted small">{p.mission || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Vision */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIndustry size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Vision</strong>
                <div className="text-muted small">{p.vision || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Market Problem */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Market Problem</strong>
                <div className="text-muted small">
                  {p.identifiedMarketProblem || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Solution */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Solution</strong>
                <div className="text-muted small">{p.solution || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Target Audience */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUsers size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Target Audience</strong>
                <div className="text-muted small">
                  {p.targetAudience || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Revenue Streams */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Revenue Streams</strong>
                <div className="text-muted small">
                  {p.revenueStreams || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Pricing Strategy */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChartLine size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Pricing Strategy</strong>
                <div className="text-muted small">
                  {p.pricingStrategy || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Revenue Forecast */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChartLine size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Revenue Forecast</strong>
                <div className="text-muted small">
                  {p.revenueForecast || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Profit Margins */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPercentage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Profit Margins</strong>
                <div className="text-muted small">{p.profitMargins || "-"}</div>
              </div>
            </div>
          </Col>

          {/* Break Even Analysis */}
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Break-Even Margins</strong>
                <div className="text-muted small">
                  {p.breakEvenMargins || "-"}
                </div>
              </div>
            </div>
          </Col>

          {/* Use of Funds */}
          <Col md={12} className="mb-3">
            <div className="d-flex align-items-start">
              <FaDollarSign size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>Use of Funds</strong>
                <div className="text-muted small">{p.useOfFunds || "-"}</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Team Members */}
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">Team Members</h5>
        {isTeamLoading ? (
          <div className="text-center py-2" style={LOADING_MIN_HEIGHT_STYLE}>
            <Spinner animation="border" size="sm" /> Loading team members...
          </div>
        ) : teamMembersData?.teamMembers?.length ? (
          <Row>
            {teamMembersData.teamMembers.map((member) => (
              <Col md={3} sm={6} className="mb-3" key={member.id}>
                <Card className="shadow-sm border-0 h-100 rounded-3 text-center">
                  <Card.Body>
                    <Card.Title className="fw-semibold mb-1 text-dark">
                      {member.firstName} {member.lastName}
                    </Card.Title>
                    <Card.Subtitle className="text-muted mb-2">
                      {member.role}
                    </Card.Subtitle>
                    <Card.Text className="small text-secondary mb-0">
                      <strong>Experience:</strong> {member.experience || "N/A"}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center text-muted">No team members found</div>
        )}
      </div>

      {/* Pitch Files */}
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">Pitch Files</h5>
        {isFilesLoading ? (
          <div className="text-center py-2" style={LOADING_MIN_HEIGHT_STYLE}>
            <Spinner animation="border" size="sm" /> Loading files...
          </div>
        ) : filesUploaded.length ? (
          <Table
            striped
            bordered
            hover
            responsive
            className="align-middle shadow-sm"
          >
            <thead className="table-light">
              <tr>
                <th>File Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filesUploaded.map((file) => (
                <tr key={file.id}>
                  <td>
                    <FaFilePdf className="me-2 text-danger" />
                    {file.name}
                  </td>
                  <td>{file.type}</td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      className="border-0 rounded-circle me-2"
                      style={ICON_BUTTON_STYLE_30}
                      onClick={() => downloadFile(file.id)}
                    >
                      <BsFileArrowDown />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center text-muted">No files found</div>
        )}
      </div>
    </div>
  );
}

const ViewPitchMemo = memo(ViewPitch);
ViewPitchMemo.displayName = "ViewPitch";
export default ViewPitchMemo;
